import { useAuth0 } from "@auth0/auth0-react";
import { useMutation } from "@tanstack/react-query";
import {
  DayAvailabilities,
  SpecificAvailabilities,
  useAvailabilitySheet,
} from "./store";
import request from "@/lib/api/handler";
import { weekdayList } from "@/types";
import { useCandidateAvailabilityQuery } from "@/queries/candidate/availability";
import { useProfileQuery } from "@/queries/user/profile";
import { QueryKeys, useInvalidateQuery } from "@/queries/utils";

export const useSaveCandidateAvailability = (
  openingID: string,
  interviewID: string,
) => {
  const { getAccessTokenSilently } = useAuth0();
  const { profile } = useProfileQuery();
  const { candidateAvailability: old } = useCandidateAvailabilityQuery(
    openingID,
    interviewID,
  );
  const invalidate = useInvalidateQuery();

  const { isPending, error, isSuccess, mutate } = useMutation({
    mutationFn: async () => {
      const { timezone, dayAvailabilities, specificAvailabilities } =
        useAvailabilitySheet.getState();

      // Filter out placeholder entries
      for (let i = 0; i < dayAvailabilities.length; i++) {
        dayAvailabilities[i] =
          dayAvailabilities[i]?.filter((e) => !e.placeholder) ?? [];
      }

      const promises: Promise<void>[] = [];
      const token = await getAccessTokenSilently();
      const uid = profile?.id;
      if (!uid) {
        throw new Error("No user ID found");
      }

      dayAvailabilities.forEach((d, i) => {
        d.forEach((entry) => {
          if (!entry.id) {
            promises.push(
              dayHandler(entry, i, timezone, token, uid, "create", interviewID),
            );
            return;
          }

          const oldDay = old?.general[i]?.find((e) => e.id === entry.id);
          if (!oldDay) {
            return;
          }

          if (
            entry.startTime !== oldDay.startTime ||
            entry.endTime !== oldDay.endTime ||
            timezone !== oldDay.timezone
          ) {
            promises.push(
              dayHandler(entry, i, timezone, token, uid, "update", interviewID),
            );
          }
        });
      });

      old?.general.forEach((d, i) => {
        d.forEach((e) => {
          if (!e.id) return;

          const newDay = dayAvailabilities[i]?.find((n) => n.id === e.id);
          if (!newDay) {
            promises.push(
              dayHandler(e, i, timezone, token, uid, "delete", interviewID),
            );
          }
        });
      });

      specificAvailabilities.forEach((s) => {
        s.times.forEach((entry) => {
          if (!entry.id) {
            promises.push(
              specificHandler(
                entry,
                s.dates,
                timezone,
                token,
                uid,
                "create",
                interviewID,
              ),
            );
            return;
          }

          const oldSpecific = old?.specific.find((o) =>
            o.times.some((ot) => ot.id === entry.id),
          );

          if (!oldSpecific) {
            return;
          }

          const oldEntry = oldSpecific.times.find((e) => e.id === entry.id);
          if (!oldEntry) {
            return;
          }

          if (
            s.dates.length !== oldSpecific.dates.length ||
            !oldSpecific.dates.every((oDate) =>
              s.dates.some((sDate) => sDate === oDate),
            ) ||
            timezone !== oldEntry.timezone
          ) {
            promises.push(
              specificHandler(
                entry,
                s.dates,
                timezone,
                token,
                uid,
                "update",
                interviewID,
              ),
            );
          }
        });
      });

      old?.specific.forEach((s) => {
        s.times.forEach((entry) => {
          if (!entry.id) return;

          const stillExists = specificAvailabilities.some((n) =>
            n.times.some(({ id }) => id === entry.id),
          );

          if (!stillExists) {
            promises.push(
              specificHandler(
                entry,
                s.dates,
                timezone,
                token,
                uid,
                "delete",
                interviewID,
              ),
            );
          }
        });
      });

      await Promise.allSettled(promises);
    },
    onSettled: async () => {
      await invalidate(QueryKeys.candidateOpportunities);
      await invalidate(QueryKeys.candidateAvailability(openingID, interviewID));

      useAvailabilitySheet.getState().setOpen(false);
      setTimeout(
        () => useAvailabilitySheet.getState().setSuccessOpen(true),
        100,
      );
    },
  });

  return {
    candidateAvailabilityMutPending: isPending,
    candidateAvailabilityMutError: error,
    candidateAvailabilityMutSuccess: isSuccess,
    saveCandidateAvailability: mutate,
  };
};

function getMethod(action: "create" | "update" | "delete") {
  return action === "create"
    ? "POST"
    : action === "update"
      ? "PATCH"
      : "DELETE";
}

function getURL(
  action: "create" | "update" | "delete",
  type: "specific" | "day",
  availabilityID?: string,
) {
  return action === "create"
    ? `api/interview/interview_availability`
    : `api/interview/interview_availability/${availabilityID}/${type === "day" ? "general" : "specific"}`;
}

async function specificHandler(
  entry: SpecificAvailabilities["times"][number],
  dates: SpecificAvailabilities["dates"],
  timezone: string,
  token: string,
  uid: string,
  action: "create" | "update" | "delete",
  interviewID: string,
) {
  const body =
    action === "create"
      ? {
          user: uid,
          interview: interviewID,
          dates,
          startTime: entry.startTime,
          endTime: entry.endTime,
          timezone,
        }
      : action === "update"
        ? {
            dates,
            startTime: entry.startTime,
            endTime: entry.endTime,
            timezone,
          }
        : {};

  await request({
    method: getMethod(action),
    path: `/${getURL(action, "specific", entry.id)}`,
    token,
    body: JSON.stringify(body),
  });
}

async function dayHandler(
  entry: DayAvailabilities[number],
  weekday: number,
  timezone: string,
  token: string,
  uid: string,
  action: "create" | "update" | "delete",
  interviewID: string,
) {
  const body =
    action === "create"
      ? {
          user: uid,
          interview: interviewID,
          startTime: entry.startTime,
          endTime: entry.endTime,
          timezone,
          weekday: weekdayList[weekday],
        }
      : action === "update"
        ? {
            startTime: entry.startTime,
            endTime: entry.endTime,
            timezone,
            weekday: weekdayList[weekday],
          }
        : {};

  await request({
    method: getMethod(action),
    path: `/${getURL(action, "day", entry.id)}`,
    token,
    body: JSON.stringify(body),
  });
}
